import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

const enum fields {
    source = "source",
    imageUrl = "imageUrl",
    altText = "altText",
    imageLink = "imageLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.source]: string;
        [fields.imageUrl]: string;
        [fields.altText]: string;
        [fields.imageLink]: LinkFieldValue;
    };
}

export interface ContainerStyles {
    mainContainer?: InjectableCss;
    container?: InjectableCss;
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    image?: LazyImageProps;
    img?: ImgProps;
    imageLink?: LinkPresentationProps;
    button?: InjectableCss;
}

export const containerStyles: ContainerStyles = {
    mainContainer: {
        css: css`
            display: flex;
            justify-content: flex-start;
            flex-direction: row;
            gap: 20px;
            border-radius: 6px 6px 6px 6px;
            overflow: hidden;
            [class^="sc-bczRLJ"] {
                width: 100%;
            }
        `,
    },
};

export const imageStyles: ImageStyles = {
    image: {
        css: css`
            max-width: 100%;
            height: auto;
        `,
    },
    img: {
        css: css`
            max-width: 100%;
            height: auto;
            cursor: pointer;
            width: 100%;
        `,
    },
    imageLink: {
        css: css`
            border-radius: 6px;
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            position: relative;
            [class^="StyledImg"] {
                border-radius: 6px;
                height: 100%;
            }
        `,
    },
};

const YoutubeContainer: React.FC<OwnProps> = ({ fields }) => {
    const { url } = useGetLink(fields.imageLink);
    const styles = useMergeStyles("image", imageStyles);
    const [shown, setShown] = React.useState(false);

    const image = (
        <Img
            className="img"
            src={fields.imageUrl}
            altText={fields.altText}
            onClick={() => setShown(!shown)}
            loading="lazy"
            {...styles.img}
        />
    );
    return (
        <>
            <StyledWrapper {...containerStyles.mainContainer}>
                <StyledWrapper className="img-wrapper" {...styles.wrapper}>
                    {shown ? <VideoModal src={fields.source} /> : null}
                    {url ? (
                        <>
                            <Link className="img-link" href={url} {...styles.imageLink}>
                                {image}
                            </Link>
                        </>
                    ) : (
                        image
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const VideoModal = (props: { src: string | undefined }) => {
    return (
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, width: "100%", height: "100%" }}>
            <iframe
                title={props.src}
                allow="autoplay"
                allowFullScreen
                frameBorder="0"
                height="100%"
                src={props.src}
                width="100%"
            />
        </div>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: YoutubeContainer,
    definition: {
        group: "Common",
        displayName: "YoutubeContainer",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.source,
                editorTemplate: "MultilineTextField",
                displayName: "Video URL",
                defaultValue: "",
            },
            {
                name: fields.imageUrl,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.altText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.imageLink,
                fieldType: "General",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
